import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  InputAdornment
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { motion } from 'framer-motion';
import axios from 'axios';
import config from '../config';
import { Link } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Successmark from '../assets/Successmark.svg';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';

const useStyles = makeStyles(() => ({
  rootContainer: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'white',
    padding: '16px',
    overflow: 'hidden',
    fontFamily: 'Open Sans, Poppins, Inter, sans-serif',
  },
  resetPasswordCard: {
    width: '100%',
    position: 'relative',
    background: 'white',
    overflow: 'hidden',
    borderRadius: 30,
    minHeight: '50rem',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '40px 20px',
    '@media (max-width: 768px)': {
      borderRadius: 0,
      boxShadow: 'none',
      minHeight: '100vh',
      padding: '20px',
    },
  },
  // OTP Verification Styles
  title: {
    color: '#C084FC',
    fontSize: 22,
    fontFamily: 'Poppins',
    fontWeight: '700',
    wordWrap: 'break-word',
    textAlign: 'center',
    marginBottom: '8px',
  },
  subtitle: {
    color: '#B6B6B6',
    fontSize: 18,
    fontFamily: 'Poppins',
    fontWeight: '500',
    wordWrap: 'break-word',
    textAlign: 'center',
    marginBottom: '32px',
  },
  otpContainer: {
    display: 'flex',
    gap: 8,
    marginBottom: '24px',
    justifyContent: 'center',
    flexWrap: 'wrap',
    maxWidth: '100%',
    padding: '0 10px',
  },
  otpBox: {
    width: 50,
    height: 50,
    borderRadius: 15,
    border: '1.60px #DDDDDD solid',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 18,
    fontFamily: 'Poppins',
    fontWeight: '700',
    color: '#757575',
    background: 'white',
    cursor: 'text',
    transition: 'all 0.3s ease',
    position: 'relative',
    flexShrink: 0,
    '&.active': {
      border: '1.80px #C084FC solid',
    },
    '&.filled': {
      border: '1.80px #C084FC solid',
    },
  },
  otpInput: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    opacity: 0,
    cursor: 'text',
    textAlign: 'center',
    fontSize: 18,
    fontFamily: 'Poppins',
    fontWeight: '700',
    border: 'none',
    outline: 'none',
    background: 'transparent',
    zIndex: 1,
  },
  resendOtp: {
    color: '#009DFF',
    fontSize: 18,
    fontFamily: 'Poppins',
    fontWeight: '500',
    wordWrap: 'break-word',
    cursor: 'pointer',
    textAlign: 'center',
    marginBottom: '24px',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  confirmButton: {
    width: '100%',
    height: 51,
    background: 'linear-gradient(90deg, rgba(192, 132, 252, 0.50) 0%, rgba(114.29, 78.57, 150, 0.50) 100%)',
    boxShadow: '0px 9px 14px rgba(192, 132, 252, 0.20)',
    borderRadius: 10.40,
    border: 'none',
    color: 'white',
    fontSize: 18,
    fontFamily: 'Poppins',
    fontWeight: '700',
    cursor: 'pointer',
    marginTop: '16px',
    '&:hover': {
      background: 'linear-gradient(90deg, rgba(192, 132, 252, 0.70) 0%, rgba(114.29, 78.57, 150, 0.70) 100%)',
    },
    '&:disabled': {
      background: '#E0E0E0',
      color: '#9E9E9E',
    },
  },
  // Set New Password Styles
  setPasswordTitle: {
    color: '#C084FC',
    fontSize: 18,
    fontFamily: 'Open Sans',
    fontWeight: '600',
    lineHeight: '19.10px',
    wordWrap: 'break-word',
    textAlign: 'center',
    marginBottom: '8px',
  },
  setPasswordSubtitle: {
    color: '#989898',
    fontSize: 15.28,
    fontFamily: 'Inter',
    fontWeight: '500',
    wordWrap: 'break-word',
    textAlign: 'center',
    marginBottom: '32px',
  },
  passwordLabel: {
    color: '#C084FC',
    fontSize: 16,
    fontFamily: 'Open Sans',
    fontWeight: '600',
    lineHeight: '19.10px',
    wordWrap: 'break-word',
    textAlign: 'left',
    width: '100%',
    marginBottom: '8px',
  },
  confirmPasswordLabel: {
    color: '#C084FC',
    fontSize: 16,
    fontFamily: 'Open Sans',
    fontWeight: '600',
    lineHeight: '19.10px',
    wordWrap: 'break-word',
    textAlign: 'left',
    width: '100%',
    marginBottom: '8px',
  },
  passwordInput: {
    width: '100%',
    height: 50,
    marginBottom: '24px',
    '& .MuiOutlinedInput-root': {
      borderRadius: 10,
      border: '1px black solid',
      '& fieldset': {
        border: '1px black solid',
      },
      '&:hover fieldset': {
        border: '1px black solid',
      },
      '&.Mui-focused fieldset': {
        border: '1px black solid',
      },
    },
    '& .MuiInputBase-input': {
      padding: '15px 16px',
      fontSize: 14,
      fontFamily: 'Open Sans',
      fontWeight: '400',
      color: '#545454',
    },
  },
  confirmPasswordInput: {
    width: '100%',
    height: 50,
    marginBottom: '24px',
    '& .MuiOutlinedInput-root': {
      borderRadius: 10,
      border: '1px black solid',
      '& fieldset': {
        border: '1px black solid',
      },
      '&:hover fieldset': {
        border: '1px black solid',
      },
      '&.Mui-focused fieldset': {
        border: '1px black solid',
      },
    },
    '& .MuiInputBase-input': {
      padding: '15px 16px',
      fontSize: 14,
      fontFamily: 'Open Sans',
      fontWeight: '400',
      color: '#545454',
    },
  },
  updatePasswordButton: {
    width: '100%',
    height: 51,
    background: 'linear-gradient(90deg, #C084FC 0%, #724F96 100%)',
    boxShadow: '0px 9px 14px rgba(192, 132, 252, 0.20)',
    borderRadius: 10.40,
    border: 'none',
    color: 'white',
    fontSize: 18,
    fontFamily: 'Poppins',
    fontWeight: '700',
    cursor: 'pointer',
    marginTop: '16px',
    '&:hover': {
      background: 'linear-gradient(90deg, #B574E6 0%, #6A4A8A 100%)',
    },
    '&:disabled': {
      background: '#E0E0E0',
      color: '#9E9E9E',
    },
  },
  // Success State Styles
  successIcon: {
    width: 62,
    height: 62,
    background: 'linear-gradient(90deg, #C084FC 0%, #724F96 100%)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  successTitle: {
    textAlign: 'center',
    color: '#C084FC',
    fontSize: 18.32,
    fontFamily: 'Open Sans',
    fontWeight: '600',
    lineHeight: '18.32px',
    wordWrap: 'break-word',
    width: '100%',
    margin: '0 auto',
  },
  successMessage: {
    width: '100%',
    maxWidth: '400px',
    textAlign: 'center',
    color: '#989898',
    fontSize: 16,
    fontFamily: 'Open Sans',
    fontWeight: '400',
    lineHeight: '24px',
    wordWrap: 'break-word',
    margin: '0 auto',
  },
  signInButton: {
    width: '100%',
    maxWidth: '300px',
    height: 51,
    background: 'linear-gradient(90deg, #C084FC 0%, #724F96 100%)',
    boxShadow: '0px 9px 14px rgba(192, 132, 252, 0.20)',
    borderRadius: 10.40,
    border: 'none',
    color: 'white',
    fontSize: 18,
    fontFamily: 'Poppins',
    fontWeight: '700',
    cursor: 'pointer',
    margin: '0 auto',
    '&:hover': {
      background: 'linear-gradient(90deg, #B574E6 0%, #6A4A8A 100%)',
    },
  },
  backLink: {
    position: 'absolute',
    bottom: 60,
    left: '50%',
    transform: 'translateX(-50%)',
    color: '#C084FC',
    textDecoration: 'none',
    fontSize: 14,
    fontFamily: 'Open Sans',
    fontWeight: '500',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  errorText: {
    color: '#f44336',
    fontSize: 14,
    textAlign: 'center',
    marginTop: 8,
    fontFamily: 'Open Sans',
  },
  successText: {
    color: '#4caf50',
    fontSize: 14,
    textAlign: 'center',
    marginTop: 8,
    fontFamily: 'Open Sans',
  },
  loadingSpinner: {
    color: 'white',
  },
}));

const ResetPassword = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [step, setStep] = useState('verify'); // 'verify', 'reset', 'success'
  const [token, setToken] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeOtpIndex, setActiveOtpIndex] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const otpRefs = [React.useRef(), React.useRef(), React.useRef(), React.useRef(), React.useRef(), React.useRef()];

  const { contactInfo, type } = location.state || {};

  useEffect(() => {
    if (!contactInfo) {
      navigate('/forgot-password');
    }
  }, [contactInfo, navigate]);

  // Auto-focus first OTP input when component mounts
  useEffect(() => {
    if (step === 'verify') {
      setActiveOtpIndex(0);
      // Focus the first input after a short delay to ensure DOM is ready
      setTimeout(() => {
        if (otpRefs[0].current) {
          otpRefs[0].current.focus();
        }
      }, 100);
    }
  }, [step]);

  // Monitor step changes for debugging
  useEffect(() => {
    console.log('Step changed to:', step);
    console.log('Current step state:', step);
    console.log('Should render success state:', step === 'success');
  }, [step]);

  const handleOtpChange = (index, value) => {
    if (value.length <= 1) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      
      // Move to next input if value is entered
      if (value && index < 5) {
        setActiveOtpIndex(index + 1);
        // Focus the next input
        setTimeout(() => {
          if (otpRefs[index + 1].current) {
            otpRefs[index + 1].current.focus();
          }
        }, 10);
      }
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      const newOtp = [...otp];
      
      // If current input has a value, clear it and stay on current input
      if (newOtp[index]) {
        newOtp[index] = '';
        setOtp(newOtp);
        setActiveOtpIndex(index);
        // Focus the current input after clearing
        setTimeout(() => {
          if (otpRefs[index].current) {
            otpRefs[index].current.focus();
            otpRefs[index].current.setSelectionRange(0, 0);
          }
        }, 10);
      } 
      // If current input is empty, move to previous input and clear it
      else if (index > 0) {
        newOtp[index - 1] = '';
        setOtp(newOtp);
        setActiveOtpIndex(index - 1);
        // Focus the previous input
        setTimeout(() => {
          if (otpRefs[index - 1].current) {
            otpRefs[index - 1].current.focus();
            otpRefs[index - 1].current.setSelectionRange(0, 0);
          }
        }, 10);
      }
    }
  };

  const handleOtpClick = (index) => {
    setActiveOtpIndex(index);
    // Focus the clicked input immediately
    if (otpRefs[index].current) {
      otpRefs[index].current.focus();
      // Set cursor to beginning of input
      otpRefs[index].current.setSelectionRange(0, 0);
    }
  };

  const handleOtpFocus = (index) => {
    setActiveOtpIndex(index);
    // Ensure cursor is at the beginning when focusing
    if (otpRefs[index].current) {
      otpRefs[index].current.setSelectionRange(0, 0);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const otpString = otp.join('');
    if (otpString.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    setIsLoading(true);

    try {
      const payload = type === 'email' 
        ? { email: contactInfo, otp: otpString }
        : { phone: contactInfo, otp: otpString };

      const response = await axios.post(`${config.API_BASE_URL}/verify-otp`, payload, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      if (response.data.token) {
        setSuccess('OTP verified successfully');
        setToken(response.data.token);
        setStep('reset');
      } else {
        setError('Invalid response from server');
      }
    } catch (error) {
      console.error('OTP verification error:', error);
      setError(error.response?.data?.error || 'Failed to verify OTP');
      // Clear OTP inputs when verification fails to allow re-entry
      setOtp(['', '', '', '', '', '']);
      setActiveOtpIndex(0);
      // Focus the first input for easy re-entry
      setTimeout(() => {
        if (otpRefs[0].current) {
          otpRefs[0].current.focus();
        }
      }, 100);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!token) {
      setError('Invalid session. Please try again.');
      return;
    }

    setIsLoading(true);

    try {
      const payload = {
        token: token,
        new_password: newPassword,
        identifier: contactInfo,
        identifier_type: type
      };

      console.log('Sending reset password request with payload:', payload);

      const response = await axios.post(`${config.API_BASE_URL}/reset-password`, payload, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      console.log('Reset password response:', response.data);
      
      if (response.data.message) {
        console.log('Password reset successful, setting step to success');
        setSuccess('Password reset successfully');
        console.log('Current step before setting to success:', step);
        setStep('success');
        console.log('Step set to success, should render success state');
      } else {
        console.log('No message in response, not setting success state');
      }
    } catch (error) {
      console.error('Password reset error:', error);
      setError(error.response?.data?.error || 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setError('');
    setIsLoading(true);

    try {
      const generatePayload = type === 'email' 
        ? { email: contactInfo }
        : { phone: contactInfo };

      await axios.post(`${config.API_BASE_URL}/forgot-password`, generatePayload, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      setSuccess('OTP resent successfully');
      setOtp(['', '', '', '', '', '']);
      setActiveOtpIndex(0);
    } catch (error) {
      console.error('Error resending OTP:', error);
      setError(error.response?.data?.error || 'Failed to resend OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignIn = () => {
    navigate('/');
  };

  return (
    <div className={classes.rootContainer}>
      <div className={classes.resetPasswordCard}>
        {step === 'verify' ? (
          <>
            <IconButton
    onClick={() => navigate('/login')}
    style={{
      position: 'absolute',
      top: 'clamp(12px, 2vw, 20px)',
      left: 'clamp(12px, 2vw, 20px)',
      background: '#F5F5F5',
      borderRadius: '50%',
      padding: 'clamp(4px, 1vw, 8px)',
    }}
  >
    <ArrowBackIosNewIcon style={{ fontSize: 'clamp(16px, 2.5vw, 20px)', color: '#C084FC' }} />
  </IconButton>
            {/* Title */}
            <div className={classes.title}>
              Verification Code
            </div>

            {/* Subtitle */}
            <div className={classes.subtitle}>
              We have sent the verification code to your ID.
            </div>

            {/* OTP Input Boxes */}
            <div className={classes.otpContainer}>
              {[0, 1, 2, 3, 4, 5].map((index) => (
                <div
                  key={index}
                  className={`${classes.otpBox} ${
                    activeOtpIndex === index ? 'active' : ''
                  } ${otp[index] ? 'filled' : ''}`}
                  onClick={() => handleOtpClick(index)}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  {otp[index]}
                  <input
                    type="text"
                    className={classes.otpInput}
                    value={otp[index]}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    onFocus={() => handleOtpFocus(index)}
                    onClick={(e) => e.stopPropagation()}
                    maxLength={1}
                    disabled={isLoading}
                    ref={otpRefs[index]}
                  />
                </div>
              ))}
            </div>

            {/* Resend OTP */}
            <div className={classes.resendOtp} onClick={handleResendOtp}>
              Resend OTP?
            </div>

            {/* Confirm Button */}
            <button
              className={classes.confirmButton}
              onClick={handleVerifyOtp}
              disabled={isLoading || otp.join('').length !== 6}
            >
              {isLoading ? (
                <CircularProgress size={20} className={classes.loadingSpinner} />
              ) : (
                'Confirm'
              )}
            </button>
          </>
        ) : step === 'reset' ? (
          <>
            {console.log('Rendering reset step')}
            {/* Set New Password Form */}
            <div className={classes.setPasswordTitle}>
              Set a new password
            </div>

            <div className={classes.setPasswordSubtitle}>
              Create a new password.
            </div>

            <div className={classes.passwordLabel}>
              Password
            </div>

            <TextField
              className={classes.passwordInput}
              fullWidth
              type={showPassword ? 'text' : 'password'}
              variant="outlined"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={isLoading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <div className={classes.confirmPasswordLabel}>
              Confirm Password
            </div>

            <TextField
              className={classes.confirmPasswordInput}
              fullWidth
              type={showConfirmPassword ? 'text' : 'password'}
              variant="outlined"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={isLoading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Update Password Button */}
            <button
              className={classes.updatePasswordButton}
              onClick={handleResetPassword}
              disabled={isLoading || !newPassword || !confirmPassword}
            >
              {isLoading ? (
                <CircularProgress size={20} className={classes.loadingSpinner} />
              ) : (
                'Update Password'
              )}
            </button>
          </>
        ) : step === 'success' ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '90vh',
            width: '100%',
            padding: '20px',
            boxSizing: 'border-box',
            textAlign: 'center',
            gap: '1rem'
          }}>
            {console.log('Rendering success step')}
            {/* Success State */}
            <div className={classes.successIcon}>
              <img src={Successmark} alt="Success" style={{ width: '100%', height: '100%' }} />
            </div>

            <div className={classes.successTitle}>
              Successful
            </div>

            <div className={classes.successMessage}>
              Congratulations! Your password has been changed. Click here to login
            </div>

            {/* Sign In Button */}
            <button
              className={classes.signInButton}
              onClick={handleSignIn}
            >
              Sign In
            </button>
          </div>
        ) : (
          <>
            {console.log('Rendering null state, step is:', step)}
            <div>Unknown step: {step}</div>
          </>
        )}

        {/* Error Message */}
        {error && (
          <div style={{ 
            position: 'absolute', 
            bottom: 100, 
            left: '50%', 
            transform: 'translateX(-50%)',
            width: '80%',
            textAlign: 'center'
          }}>
            <Typography className={classes.errorText}>
              {error}
            </Typography>
          </div>
        )}

        {/* Success Message */}
        {success && step !== 'success' && (
          <div style={{ 
            position: 'absolute', 
            bottom: 100, 
            left: '50%', 
            transform: 'translateX(-50%)',
            width: '80%',
            textAlign: 'center'
          }}>
            <Typography className={classes.successText}>
              {success}
            </Typography>
          </div>
        )}

        {/* Back to Login Link - Only show in verify and reset steps */}
      </div>
    </div>
  );
};

export default ResetPassword; 